import { WithPartialValues } from "@biggeo/bg-utils";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import isEqual from "lodash/isEqual";
import { match } from "ts-pattern";

import { MapLayoutTabs } from "../../../layouts/MapLayout";
import { MapContextType } from "../context";
import { MapContextDataset } from "../context/types";

export const DEFAULT_VIEWPORT = {
    latBounds: {
        min: -90,
        max: 90,
    },
    lngBounds: {
        min: -180,
        max: 180,
    },
};

export const resizeMap = (
    input: Pick<MapContextType, "map" | "isMapLoaded">
) => {
    window.setTimeout(() => {
        if (input.map.current && input.isMapLoaded) {
            input.map.current?.resize();
        }
    }, 200);
};

export const removeMap = (
    input: {
        isSavedViewPage: boolean;
        geocode?: React.MutableRefObject<MapboxGeocoder | null>;
    } & Pick<MapContextType, "map" | "draw" | "isMapLoaded" | "dispatch">
) => {
    const { isSavedViewPage, geocode, map, draw, isMapLoaded, dispatch } =
        input;

    const isSavedViewStyleLoaded = isSavedViewPage ? isMapLoaded : true;

    if (map.current && isSavedViewStyleLoaded) {
        if (draw.current) {
            // Cleanup the context when you leave the map page or switch tabs.
            const hasDrawControl = map.current.hasControl(draw.current);

            if (hasDrawControl) {
                map.current.removeControl(draw.current);
            }

            dispatch?.({
                type: "SET_MAP",
                values: {
                    draw: { current: null },
                },
            });
        }

        if (geocode?.current) {
            const hasGeocodeControl = map.current.hasControl(geocode.current);

            if (hasGeocodeControl) {
                map.current.removeControl(geocode.current);
            }
        }

        const geocoderElement = document.getElementById("geocoder");

        if (geocoderElement) {
            geocoderElement.remove();
        }

        map.current.remove();

        dispatch?.({
            type: "SET_MAP",
            values: {
                map: { current: null },
                isMapLoaded: false,
            },
        });
    }
};

export const getMapTab = (mapTab: string | null): MapLayoutTabs => {
    return match<string | null, MapLayoutTabs>(mapTab)
        .with("dataset", () => "datasets")
        .with("shapeLayers", () => "shapeLayers")
        .with("boundaries", () => "boundaries")
        .with("maps", () => "maps")
        .with("configuration", () => "configuration")
        .otherwise(() => "datasets");
};

export const handleDatasetTable = (i: {
    state: boolean;
    datasets: MapContextDataset[];
    updateDataset: (values: {
        dataSourceId: string;
        dataset: Partial<WithPartialValues<MapContextDataset>>;
    }) => void;
}) => {
    const { state, datasets, updateDataset } = i;

    const selectedDatasets = pipe(
        datasets,
        A.filter((d) => isEqual(d.isSelected, true))
    );

    pipe(
        selectedDatasets,
        A.map((d) =>
            updateDataset({
                dataSourceId: d.dataSource.id,
                dataset: {
                    isTableViewed: state,
                },
            })
        )
    );
};
